import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Input } from '../components/ui/input';
import { Lightbulb, Target, Users, BarChart3, Clock, RefreshCw, HelpCircle, ExternalLink } from 'lucide-react';
import api from '../services/api';
import EnginePill from '../components/EnginePill';
import AIChat from '../components/AIChat';

const OptimiseContent = () => {
  const [searchParams] = useSearchParams();
  const [websites, setWebsites] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(searchParams.get('websiteId') || '');
  const [selectedKeyword, setSelectedKeyword] = useState(searchParams.get('keywordId') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contentBrief, setContentBrief] = useState(null);
  const [dataFreshness, setDataFreshness] = useState(null);
  const [targetUrl, setTargetUrl] = useState(searchParams.get('targetUrl') || '');
  const [targetFetchError, setTargetFetchError] = useState(null);

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const response = await api.get('/websites');
        setWebsites(response.data.websites);
      } catch (err) {
        setError('Failed to fetch websites');
      }
    };

    fetchWebsites();
  }, []);

  useEffect(() => {
    const fetchKeywords = async () => {
      if (!selectedWebsite) {
        setKeywords([]);
        return;
      }

      try {
        const response = await api.get(`/keywords/website/${selectedWebsite}`);
        setKeywords(response.data.keywords);
      } catch (err) {
        setError('Failed to fetch keywords');
      }
    };

    fetchKeywords();
  }, [selectedWebsite]);

  const handleGenerateContentBrief = async () => {
    if (!selectedKeyword) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/content-briefs/generate', {
        keywordId: selectedKeyword,
        targetUrl: targetUrl.trim() || undefined
      });

      // The new API returns brief_data in the brief object
      if (response.data.brief && response.data.brief.brief_data) {
        setContentBrief(response.data.brief.brief_data);
      } else {
        setContentBrief(response.data.brief || response.data);
      }
      setDataFreshness(response.data.dataFreshness || null);
      setTargetFetchError(response.data.targetFetchError || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate content brief');
    } finally {
      setLoading(false);
    }
  };

  const renderPlainTextBrief = (text) => {
    return (
      <div className="whitespace-pre-wrap text-sm leading-relaxed">
        {text.split('\n').map((line, index) => (
          <p key={index} className="mb-2">
            {line}
          </p>
        ))}
      </div>
    );
  };

  const renderStructuredBrief = (brief) => {
    return (
      <div className="space-y-6">
        {/* Metadata */}
        {brief.metadata && (
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Citations</p>
                <p className="text-2xl font-bold">{brief.metadata.totalCitations}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Brand Citations</p>
                <p className="text-2xl font-bold text-green-600">{brief.metadata.brandCitations}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Competitor Citations</p>
                <p className="text-2xl font-bold text-orange-600">{brief.metadata.competitorCitations}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Page-specific recommendations — shown only when a target URL was analysed */}
        {brief.pageRecommendations && (
          <Card className="border-blue-200 bg-blue-50/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                Recommendations for your page
              </CardTitle>
              {brief.pageRecommendations.summary && (
                <CardDescription className="text-foreground/80 pt-1">{brief.pageRecommendations.summary}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {brief.pageRecommendations.quickWins && brief.pageRecommendations.quickWins.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Quick wins</p>
                  <ul className="text-sm space-y-1.5 list-disc pl-5">
                    {brief.pageRecommendations.quickWins.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
              {brief.pageRecommendations.missingTopics && brief.pageRecommendations.missingTopics.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Missing topics</p>
                  <ul className="text-sm space-y-1.5 list-disc pl-5">
                    {brief.pageRecommendations.missingTopics.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              )}
              {brief.pageRecommendations.weakSections && brief.pageRecommendations.weakSections.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Weak sections</p>
                  <ul className="text-sm space-y-1.5 list-disc pl-5">
                    {brief.pageRecommendations.weakSections.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {brief.pageRecommendations.structuralChanges && brief.pageRecommendations.structuralChanges.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Structural changes</p>
                  <ul className="text-sm space-y-1.5 list-disc pl-5">
                    {brief.pageRecommendations.structuralChanges.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Entities */}
        {brief.entities && brief.entities.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Key Entities to Cover
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {brief.entities.map((entity, i) => (
                  <Badge key={i} variant="default">{entity}</Badge>
                ))}
              </div>
              {brief.entityCoverage && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm"><strong>Analysis:</strong> {brief.entityCoverage.description}</p>
                  <p className="text-sm text-muted-foreground"><strong>Recommendations:</strong> {brief.entityCoverage.recommendations}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Title and Objective - Legacy support */}
        {brief.title && (
          <div className="space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-2">Recommended Title</h3>
              <p className="text-base font-medium bg-muted p-3 rounded-md">{brief.title}</p>
            </div>
            {brief.objective && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-2">Content Objective</h3>
                <p className="text-muted-foreground">{brief.objective}</p>
              </div>
            )}
          </div>
        )}

        {/* Main Sections */}
        {brief.mainSections && brief.mainSections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Content Structure
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Recommended sections optimized for LLM citation and authority</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {brief.mainSections.map((section, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <h4 className="font-semibold text-base">{section.section}</h4>
                    <Badge variant="secondary">Section {index + 1}</Badge>
                  </div>

                  {section.purpose && (
                    <p className="text-sm text-muted-foreground italic">{section.purpose}</p>
                  )}

                  {section.keyPoints && section.keyPoints.length > 0 && (
                    <div>
                      <h5 className="font-medium text-sm mb-2">Key Points:</h5>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {section.keyPoints.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.entities && section.entities.length > 0 && (
                    <div>
                      <h5 className="font-medium text-sm mb-2">Key Entities:</h5>
                      <div className="flex flex-wrap gap-1">
                        {section.entities.map((entity, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {entity}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {section.questions && section.questions.length > 0 && (
                    <div>
                      <h5 className="font-medium text-sm mb-2">Questions to Answer:</h5>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        {section.questions.map((question, i) => (
                          <li key={i}>{question}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Key Entities */}
          {brief.keyEntities && brief.keyEntities.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" />
                  Key Entities
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Important brands, people, and concepts to mention for authority</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {brief.keyEntities.map((entity, index) => (
                    <Badge key={index} variant="default">
                      {entity}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data Points */}
          {brief.dataPoints && brief.dataPoints.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="h-4 w-4" />
                  Essential Data
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Statistics and data points that strengthen credibility</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {brief.dataPoints.map((point, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      {point}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* LLM Optimization */}
        {brief.llmOptimization && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                LLM Optimization Strategy
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Specific tactics to increase likelihood of LLM citation</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(brief.llmOptimization).map(([key, value]) => (
                <div key={key}>
                  <h5 className="font-medium capitalize mb-2">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h5>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {Array.isArray(value) ? value.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 flex-shrink-0"></div>
                        {item}
                      </li>
                    )) : (
                      <li>{value}</li>
                    )}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Content Specs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {brief.contentLength && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Content Length
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-primary">{brief.contentLength}</p>
              </CardContent>
            </Card>
          )}

          {brief.updateFrequency && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Update Frequency
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-primary">{brief.updateFrequency}</p>
              </CardContent>
            </Card>
          )}

          {brief.competitorGaps && brief.competitorGaps.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="success" className="w-full">
                  {brief.competitorGaps.length} gap{brief.competitorGaps.length !== 1 ? 's' : ''} identified
                </Badge>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Competitor Gaps */}
        {brief.competitorGaps && brief.competitorGaps.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Competitive Opportunities
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Areas where competitors are lacking that you can dominate</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {brief.competitorGaps.map((gap, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-green-800">{gap}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* PAA Questions */}
        {brief.paaQuestions && brief.paaQuestions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                People Also Ask (PAA) Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {brief.paaQuestions.map((question, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 border-l-2 border-primary pl-3">
                    <span className="text-sm">{question}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Suggested FAQs */}
        {brief.suggestedFaqs && brief.suggestedFaqs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                Suggested FAQs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {brief.suggestedFaqs.map((faq, i) => (
                <div key={i} className="border-l-4 border-blue-500 pl-4 py-2">
                  <p className="font-semibold text-sm mb-1">{faq.question}</p>
                  <p className="text-sm text-muted-foreground mb-1">{faq.answer}</p>
                  {faq.source && (
                    <p className="text-xs text-muted-foreground italic">Source: {faq.source}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Content Structure */}
        {brief.contentStructure && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Recommended Content Structure
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {brief.contentStructure.recommendedFormat && (
                <div>
                  <p className="font-semibold text-sm">Format</p>
                  <Badge variant="default">{brief.contentStructure.recommendedFormat}</Badge>
                </div>
              )}
              {brief.contentStructure.estimatedWordCount && (
                <div>
                  <p className="font-semibold text-sm">Word Count</p>
                  <p className="text-muted-foreground">{brief.contentStructure.estimatedWordCount}</p>
                </div>
              )}
              {brief.contentStructure.toneAndStyle && (
                <div>
                  <p className="font-semibold text-sm">Tone & Style</p>
                  <p className="text-muted-foreground">{brief.contentStructure.toneAndStyle}</p>
                </div>
              )}
              {brief.contentStructure.steps && brief.contentStructure.steps.length > 0 && (
                <div>
                  <p className="font-semibold text-sm mb-2">Steps to Include</p>
                  <ol className="list-decimal list-inside space-y-1">
                    {brief.contentStructure.steps.map((step, i) => (
                      <li key={i} className="text-sm">{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Optimization Tips */}
        {brief.aiOptimizationTips && brief.aiOptimizationTips.length > 0 && (
          <Card className="border-2 border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                AI Overview Optimization
              </CardTitle>
              <CardDescription>Tips to make your content AI-friendly</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {brief.aiOptimizationTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm">{tip}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Content Gaps */}
        {brief.contentGaps && brief.contentGaps.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Content Gaps & Opportunities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {brief.contentGaps.map((gap, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 border-l-2 border-orange-500 pl-3">
                    <span className="text-sm">{gap}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Competitor Summary */}
        {brief.competitorSummary && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Competitor Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {brief.competitorSummary.topCompetitors && (
                <div>
                  <p className="font-semibold text-sm mb-2">Top Competitors</p>
                  <div className="flex flex-wrap gap-2">
                    {brief.competitorSummary.topCompetitors.map((comp, i) => (
                      <Badge key={i} variant="destructive">{comp}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {brief.competitorSummary.contentPatterns && (
                <div>
                  <p className="font-semibold text-sm">Content Patterns</p>
                  <p className="text-sm text-muted-foreground">{brief.competitorSummary.contentPatterns}</p>
                </div>
              )}
              {brief.competitorSummary.strengthsAndWeaknesses && (
                <div>
                  <p className="font-semibold text-sm">Strengths & Weaknesses</p>
                  <p className="text-sm text-muted-foreground">{brief.competitorSummary.strengthsAndWeaknesses}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight">Optimize Content</h1>
            <EnginePill />
          </div>
          <p className="text-muted-foreground mt-2">
            Generate LLM-optimized content briefs based on keyword citation analysis
          </p>
          <p className="text-sm text-muted-foreground mt-2">These are untested content hypotheses. Use <a className="underline" href="/workflow">AI Workflow</a> to test a candidate, review the evidence and create an approved content plan.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              Generate Content Brief
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Create detailed content briefs optimized for LLM citation</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              Select a keyword to analyze its citation landscape and generate a comprehensive content strategy
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Website</label>
                <Select value={selectedWebsite} onValueChange={(value) => {
                  setSelectedWebsite(value);
                  setSelectedKeyword('');
                  setContentBrief(null);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select website" />
                  </SelectTrigger>
                  <SelectContent>
                    {websites.map((website) => (
                      <SelectItem key={website.id} value={website.id}>
                        {website.brandName} ({website.domain})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Keyword</label>
                <Select
                  value={selectedKeyword}
                  onValueChange={(value) => {
                    setSelectedKeyword(value);
                    setContentBrief(null);
                  }}
                  disabled={!selectedWebsite}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select keyword" />
                  </SelectTrigger>
                  <SelectContent>
                    {keywords.map((keyword) => (
                      <SelectItem key={keyword.id} value={keyword.id}>
                        {keyword.keyword}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 flex items-center gap-2">
                Target URL <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex"><HelpCircle className="h-3.5 w-3.5 text-muted-foreground" /></button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Paste the URL of the page you want to optimise. We'll fetch it and the brief will recommend specific changes to that page rather than generic advice.</p>
                  </TooltipContent>
                </Tooltip>
              </label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://example.com/blog/your-article"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  disabled={!selectedKeyword || loading}
                  className="flex-1"
                />
                <Button
                  onClick={handleGenerateContentBrief}
                  disabled={loading || !selectedKeyword}
                  className="shrink-0"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Analysing…
                    </>
                  ) : (
                    <>
                      <Target className="h-4 w-4 mr-2" />
                      Generate Brief
                    </>
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {contentBrief && (
          <>
            {dataFreshness?.isStale && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertDescription>
                  <strong>Heads up:</strong> the citations behind this brief are from {dataFreshness.ageDays} days ago. AI search results shift quickly, so the recommendations may be based on stale data. Re-run this keyword (or wait for the next scheduled refresh) before acting on these suggestions.
                </AlertDescription>
              </Alert>
            )}
            {targetFetchError && (
              <Alert className="border-orange-300 bg-orange-50 text-orange-900">
                <AlertDescription>
                  <strong>Couldn't fetch the target URL:</strong> {targetFetchError}. The brief was generated from citation data only — page-specific recommendations were skipped.
                </AlertDescription>
              </Alert>
            )}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Content Brief
                  <Badge variant="success">Ready</Badge>
                  {dataFreshness && !dataFreshness.isStale && (
                    <span className="text-xs font-normal text-muted-foreground">
                      · data from {dataFreshness.ageDays === 0 ? 'today' : `${dataFreshness.ageDays}d ago`}
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  Comprehensive content strategy optimized for LLM citation and authority
                </CardDescription>
              </CardHeader>
              <CardContent>
                {contentBrief.plainText
                  ? renderPlainTextBrief(contentBrief.plainText)
                  : renderStructuredBrief(contentBrief)
                }
              </CardContent>
            </Card>
          </>
        )}

        {!websites.length && (
          <Alert>
            <AlertDescription>
              No websites found. Please add websites and keywords to generate content briefs.
            </AlertDescription>
          </Alert>
        )}
        {selectedWebsite && <AIChat websiteId={selectedWebsite} />}
      </div>
    </TooltipProvider>
  );
};

export default OptimiseContent;